import React, { useState } from 'react';
import { X, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useImportSeries, ImportLimitError } from '../../hooks/useImportSeries';
import type { ResourceSeries } from '../../hooks/useResourceSeries';

type Stage = 'confirm' | 'importing' | 'done' | 'error';

interface Props {
  series: ResourceSeries;
  onClose: () => void;
}

export function ImportSeriesModal({ series, onClose }: Props) {
  const navigate = useNavigate();
  const { importFullSeries } = useImportSeries();
  const [stage, setStage] = useState<Stage>('confirm');
  const [progressMsg, setProgressMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const handleImport = async () => {
    setStage('importing');
    try {
      await importFullSeries(series.id, msg => setProgressMsg(msg));
      setStage('done');
    } catch (err) {
      if (err instanceof ImportLimitError) {
        setErrorMsg(err.message);
      } else {
        setErrorMsg(err instanceof Error ? err.message : 'Import failed. Please try again.');
      }
      setStage('error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-charcoal rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-sand/20 dark:border-slate/20">
          <h2 className="text-base font-semibold text-midnight dark:text-soft-white">Import Series</h2>
          {stage !== 'importing' && (
            <button onClick={onClose} className="p-1 rounded hover:bg-sand/20 dark:hover:bg-slate/20">
              <X className="h-4 w-4 text-slate" />
            </button>
          )}
        </div>

        <div className="px-6 py-6">
          {stage === 'confirm' && (
            <div className="space-y-4">
              <p className="text-sm text-midnight dark:text-soft-white">
                Import <span className="font-semibold">{series.title}</span> into your sermon library?
              </p>
              <ul className="text-sm text-slate dark:text-soft-white/60 space-y-1">
                <li>• {series.week_count} {series.week_count === 1 ? 'sermon' : 'sermons'} added as drafts</li>
                <li>• Sermon notes imported into editor</li>
                <li>• Graphics copied to each sermon's media</li>
                <li>• Counts as 1 of your 2 monthly imports</li>
              </ul>
            </div>
          )}

          {stage === 'importing' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <Loader2 className="h-8 w-8 animate-spin text-electric" />
              <p className="text-sm text-midnight dark:text-soft-white text-center">
                {progressMsg || 'Starting import...'}
              </p>
            </div>
          )}

          {stage === 'done' && (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle className="h-10 w-10 text-emerald-500" />
              <p className="text-sm font-semibold text-midnight dark:text-soft-white">Import complete!</p>
              <p className="text-xs text-slate dark:text-soft-white/50">
                All sermons and assets are now in your library.
              </p>
            </div>
          )}

          {stage === 'error' && (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <AlertCircle className="h-10 w-10 text-red-400" />
              <p className="text-sm font-semibold text-midnight dark:text-soft-white">Import failed</p>
              <p className="text-xs text-slate dark:text-soft-white/50">{errorMsg}</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-sand/20 dark:border-slate/20">
          {stage === 'confirm' && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm border border-sand/40 dark:border-slate/30 rounded-lg hover:bg-sand/10 text-midnight dark:text-soft-white">
                Cancel
              </button>
              <button onClick={handleImport} className="px-4 py-2 bg-electric text-white text-sm rounded-lg hover:bg-electric/90">
                Import Series
              </button>
            </>
          )}
          {stage === 'done' && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm border border-sand/40 dark:border-slate/30 rounded-lg hover:bg-sand/10 text-midnight dark:text-soft-white">
                Stay Here
              </button>
              <button
                onClick={() => navigate('/dashboard/library')}
                className="px-4 py-2 bg-electric text-white text-sm rounded-lg hover:bg-electric/90"
              >
                Go to Library
              </button>
            </>
          )}
          {stage === 'error' && (
            <button onClick={onClose} className="px-4 py-2 text-sm border border-sand/40 dark:border-slate/30 rounded-lg hover:bg-sand/10 text-midnight dark:text-soft-white">
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
